import queryString from 'query-string';
import { type configureViews, getViewByPathname, VIEWS_BY_NAME } from '/@/settings/views';

export const getLocalePrefixFromURL = () => {
    let { pathname } = window.location;
    if (!pathname.startsWith('/')) pathname = '/'.concat(pathname);
    return pathname.split('/')[1];
};

export const addLocalePrefix = (language, url) => {
    return '/'.concat(language, '/', url, '/').replace(/\/+/g, '/');
};

export const stripLocalePrefix = (path) => {
    let pathSlugs = path.split('/');

    const localeIndex = path.startsWith('/') ? 1 : 0;
    const locale = pathSlugs[localeIndex];

    if (locale.length === 2) {
        pathSlugs = pathSlugs.slice(localeIndex + 1);
    } else {
        // remove trailing and preceding forward slashes to avoid collisions
        pathSlugs = pathSlugs.join(' ').trim().split(' ');
    }

    pathSlugs = pathSlugs.join('/');
    return '/'.concat(pathSlugs);
};

export const parseParams = (params) => {
    return Object.entries(params).reduce((parsed, [key, value]) => {
        parsed[key] = Number.isNaN(Number(value)) ? value : Number(value);
        return parsed;
    }, {});
};

export const parseQuerystring = (string) => {
    const parsed = queryString.parse(string);
    return Object.entries(parsed).reduce((parsed, [key, value]) => {
        try {
            parsed[key] = JSON.parse(value);
        } catch (error) {} // eslint-disable-line no-empty
        return parsed;
    }, parsed);
};

export const stringifyQuerystring = (object) => queryString.stringify(object);

export const reverse = (name: ReturnType<typeof configureViews>['views'][number]['name'], queryData = {}) => {
    const view = VIEWS_BY_NAME[name];
    if (!view) throw new Error('ViewNotFound: '.concat(name));

    let { path } = view;

    // Add locale prefix.
    const localePrefix = getLocalePrefixFromURL();
    path = addLocalePrefix(localePrefix, path);

    const { params, qs } = queryData;

    // Replace provided params.
    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            path = path.replace(`:${key}?`, value); // replace optional parameters
            path = path.replace(`:${key}`, value); // replace mandatory parameters
        });
    }
    path = path.replace(/(:\w+\?)\//g, ''); // Remove optional params that are not set.

    // Add query string.
    if (qs) {
        Object.entries(qs).forEach(([key, value]) => {
            if (typeof value === 'object') qs[key] = JSON.stringify(value);
        });
        path = path.concat('?', queryString.stringify(qs));
    }

    return path;
};

export const parseNextUrl = (location, defaultView = 'NewHome') => {
    // Check if we have a defined next url and if it's valid
    const querystring = parseQuerystring(location.search);
    if (querystring.next) {
        const next = decodeURIComponent(querystring.next);
        const [pathname, _] = next.split('?');
        // Assume that next won't have a locale prefix
        if (getViewByPathname(pathname)) return next;
    }
    return reverse(defaultView);
};

export const encodeNextUrl = (location, defaultView) => {
    return encodeURIComponent(parseNextUrl(location, defaultView));
};

export const decodeNextUrl = (location, defaultView) => {
    return decodeURIComponent(parseNextUrl(location, defaultView));
};
